import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import { Error } from '@/components/ui/error';
import { Loading } from '@/components/ui/loading';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { useSeedProfiles, usePostCompanySeedProfiles } from '@/data/company';
import { useToast } from '@/helpers/toast';

const formSchema = z.object({
  seed_profiles: z.array(z.string()).min(1, 'Select at least one profile')
});

const SeedProfilesDialog = ({ onClose, uid, isDemo }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const seedProfiles = useSeedProfiles();
  const postCompanySeedProfiles = usePostCompanySeedProfiles();
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      seed_profiles: []
    }
  });

  const onSubmit = async (values) => {
    setApiError('');
    setIsSubmitting(true);
    try {
      await postCompanySeedProfiles(uid, { seed_profiles: values.seed_profiles });
      toast({ title: 'Seeded successfully' });
      onClose();
    } catch (err) {
      setIsSubmitting(false);
      setApiError(err.message);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Seed Company Data</DialogTitle>
          <DialogDescription>
            Select which seed profiles to create for this company. This will create a template and some sample Projects + Historical Records for that sector.
          </DialogDescription>
        </DialogHeader>
        {!isDemo && (
          <>
            <Alert variant="destructive">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle>Not a demo company</AlertTitle>
              <AlertDescription>This company is not demo; you can only seed data for demo companies.</AlertDescription>
            </Alert>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
        {isDemo && seedProfiles.isLoading && !seedProfiles.data && <Loading />}
        {isDemo && seedProfiles.error && <Error message="An error occurred loading seed profiles" />}
        {isDemo && seedProfiles.data && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-3">
                {seedProfiles.data.query.map((profileKey) => {
                  const profile = seedProfiles.data.content[profileKey];
                  return (
                    <FormField
                      key={profileKey}
                      control={form.control}
                      name="seed_profiles"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0">
                          <FormControl className="flex items-end">
                            <Checkbox
                              checked={field.value?.includes(profileKey)}
                              onCheckedChange={(checked) => {
                                const current = field.value || [];
                                field.onChange(checked ? [...current, profileKey] : current.filter((k) => k !== profileKey));
                              }}
                            />
                          </FormControl>
                          <FormLabel>{profile.label}</FormLabel>
                        </FormItem>
                      )}
                    />
                  );
                })}
                {form.formState.errors.seed_profiles && <p className="text-sm font-medium text-destructive">{form.formState.errors.seed_profiles.message}</p>}
              </div>
              {apiError && <Error message={apiError} />}
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Seed Company Data
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SeedProfilesDialog;
